import React, { useState } from 'react';
import { AppNav } from '../../components/layout/AppNav';

interface NotifPrefs {
  offerAccepted: boolean;
  offerRejected: boolean;
  linkExpiring: boolean;
}

const STORAGE_KEY_NOTIF = 'rms_notif_prefs';
const STORAGE_KEY_LANG = 'rms_language';

function loadPrefs(): NotifPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_NOTIF);
    if (raw) return JSON.parse(raw) as NotifPrefs;
  } catch { /* ignore */ }
  return { offerAccepted: true, offerRejected: true, linkExpiring: true };
}

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
  <div
    onClick={() => onChange(!checked)}
    role="switch"
    aria-checked={checked}
    tabIndex={0}
    onKeyDown={(e) => e.key === 'Enter' && onChange(!checked)}
    style={{
      width: '40px', height: '22px', borderRadius: '11px',
      background: checked ? 'var(--p)' : 'var(--border)',
      cursor: 'pointer', position: 'relative', transition: 'background .2s', flexShrink: 0,
    }}
  >
    <div style={{
      position: 'absolute', top: '3px',
      left: checked ? '19px' : '3px',
      width: '16px', height: '16px',
      borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      transition: 'left .2s',
    }} />
  </div>
);

const PrefRow: React.FC<{ label: string; description: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, description, checked, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
    <div>
      <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-1)' }}>{label}</div>
      <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '2px' }}>{description}</div>
    </div>
    <Toggle checked={checked} onChange={onChange} />
  </div>
);

export const SettingsPage: React.FC = () => {
  const [prefs, setPrefs] = useState<NotifPrefs>(loadPrefs);
  const [lang, setLang] = useState(() => localStorage.getItem(STORAGE_KEY_LANG) ?? 'en');

  const updatePref = (key: keyof NotifPrefs, val: boolean) => {
    const next = { ...prefs, [key]: val };
    setPrefs(next);
    localStorage.setItem(STORAGE_KEY_NOTIF, JSON.stringify(next));
  };

  const updateLang = (val: string) => {
    setLang(val);
    localStorage.setItem(STORAGE_KEY_LANG, val);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Settings" subtitle="Configure your preferences" />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '560px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="page-title" style={{ marginBottom: '8px' }}>Settings</div>

          {/* Notifications */}
          <div className="card">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <span className="section-title" style={{ marginBottom: 0 }}>Notification Preferences</span>
            </div>
            <div className="card-body">
              <PrefRow label="Offer Accepted" description="Notify me when a customer accepts an offer" checked={prefs.offerAccepted} onChange={(v) => updatePref('offerAccepted', v)} />
              <PrefRow label="Offer Rejected" description="Notify me when a customer rejects an offer" checked={prefs.offerRejected} onChange={(v) => updatePref('offerRejected', v)} />
              <PrefRow label="Link Expiring Soon" description="Notify me when a customer link expires within 2 days" checked={prefs.linkExpiring} onChange={(v) => updatePref('linkExpiring', v)} />
            </div>
          </div>

          {/* Language */}
          <div className="card">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <span className="section-title" style={{ marginBottom: 0 }}>Display Language</span>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', gap: '10px' }}>
                {[{ value: 'en', label: 'English' }, { value: 'ar', label: 'العربية' }].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => updateLang(value)}
                    className={lang === value ? 'btn-secondary' : 'btn-ghost'}
                    style={{ padding: '8px 20px' }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {lang === 'ar' && (
                <p style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '10px', marginBottom: 0 }}>
                  Arabic language support is coming soon.
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
